import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { RootState } from ".";
import { logout } from "./slices/authSlice";

// API Response types
interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface TraderProfile {
  id: string;
  name: string;
  status: string;
  balance: number;
  frozenBalance: number;
  transitBalance: number;
  isBlocked: boolean;
  user: {
    login: string;
    role: string;
    lastLoginAt?: string;
  };
  canConfirmManually: boolean;
}

interface TraderBalance {
  traderId: string;
  balance: number;
  frozenBalance: number;
  transitBalance: number;
  transitBalanceThreshold: number;
  availableBalance: number;
  disputeBalance: number;
  monthlyIncome: number;
  name: string;
}

interface TraderRequisite {
  id: string;
  cardNumber: string;
  holderFirstName: string;
  holderLastName: string;
  phoneNumber?: string;
  status: string;
  maxTotalVolumeLimit: number;
  methodLimits: Array<{
    id: string;
    method: string;
    minAmount: number;
    maxAmount: number;
    currency: string;
    isActive: boolean;
  }>;
}

interface PaymentMethod {
  id: string;
  method: string;
  displayName: string;
  isActive: boolean;
  priority: number;
  description?: string;
  isCrossborder?: boolean; // VP-201
}

// Новые типы для системы держателей и реквизитов
interface Currency {
  id: string;
  code: string;
  name: string;
  symbol?: string;
  isActive: boolean;
}

interface Bank {
  id: number;
  name: string;
  code: string;
  logoUrl?: string;
  countryCode?: string; // VP-201: для трансгран-банков (AM/TJ/UZ/AB)
}

interface GeoLocation {
  id: string;
  name: string;
  code: string;
  flag?: string;
}

// Типы для устройств автоматики
interface DeviceHealth {
  isHealthy: boolean;
  permissionsOk: boolean;
  notificationsOk: boolean;
  batteryOptimizationDisabled: boolean;
  backgroundServiceRunning: boolean;
  networkOk: boolean;
  airplaneMode: boolean;
  lastReportAt: Date;
}

interface DeviceInfo {
  id: string;
  holderId: string;
  platform: string;
  model: string;
  osVersion: string;
  appVersion: string;
  lastSeenAt?: Date;
  isActive: boolean;
  authStatus: string;
  health?: DeviceHealth;
  createdAt: Date;
}

interface DevicesListResponse {
  data: DeviceInfo[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface BindingTokenResponse {
  token: string;
  holderId: string;
  expiresAt: number;
  backendBaseUrl: string;
}

// VP-205: phoneNumber/countryId/country удалены — телефон теперь только
// на реквизите, страна — на самом реквизите для трансгран-методов.
interface TraderRequisiteHolder {
  id: string;
  traderId: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  currencyCode?: string;
  bankId?: number;
  tag?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  currency?: Currency;
  bank?: Bank;
  requisites?: NewTraderRequisite[];
}

// Статистика по реквизиту
interface RequisiteStatistics {
  today: {
    completedCount: number;
    pendingCount: number;
    completedAmount: number;
    pendingAmount: number;
  };
  allTime: {
    completedCount: number;
    pendingCount: number;
    completedAmount: number;
    pendingAmount: number;
  };
}

interface NewTraderRequisite {
  id: string;
  holderId: string;
  method: string;
  maskedData: string;
  maxTotalVolumeLimit?: number;
  dailyLimit?: number;
  paymentLimit?: number;
  dailyPaymentLimit?: number;
  maxParallelOrders?: number;
  cooldownSec: number;
  lastIssuedAt?: string;
  availableAt?: string;
  tag?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  currencyCode: string; // Moved from Holder
  bankId: number;       // Moved from Holder, now mandatory
  limits: Array<{
    id: string;
    method: string;
    minAmount: number;
    maxAmount: number;
    currency: string;
    isActive: boolean;
  }>;
  bank?: Bank; // Add bank property
  // Поля для редактирования (возвращаются только при GET запросе одного реквизита)
  cardNumber?: string;
  holderName?: string;
  sbpPhoneNumber?: string;
  accountNumber?: string;
  bankIdCode?: string;
  recipientName?: string;
  // Статистика по реквизиту
  statistics?: RequisiteStatistics;
}

interface CreateHolderRequest {
  firstName: string;
  lastName: string;
  middleName?: string;
  currencyCode?: string;
  bankId?: number;
  tag?: string;
  isActive?: boolean;
}

interface UpdateHolderRequest {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  currencyCode?: string;
  bankId?: number;
  tag?: string;
  isActive?: boolean;
}

interface CreateRequisiteRequest {
  method: string;
  bankId: number;       // Now mandatory
  currencyCode: string; // New field
  // VP-205: страна реквизита (только для трансгран-методов).
  countryCode?: string;
  cardNumber?: string;
  holderName?: string;
  sbpPhoneNumber?: string;
  accountNumber?: string;
  bankIdCode?: string;
  recipientName?: string;
  additionalData?: string;
  maxTotalVolumeLimit?: number;
  dailyLimit?: number;
  paymentLimit?: number;
  dailyPaymentLimit?: number;
  maxParallelOrders?: number;
  cooldownSec?: number;
  tag?: string;
  limits: Array<{
    method: string;
    minAmount: number;
    maxAmount: number;
    currency: string;
    isActive?: boolean;
  }>;
  isActive?: boolean;
}

interface GetHoldersParams {
  page?: number;
  limit?: number;
  search?: string;
  tag?: string;
  bankId?: number;
  currency?: string;
  status?: boolean;
}

interface GetRequisitesParams {
  page?: number;
  limit?: number;
  search?: string;
  method?: string;
  isActive?: boolean;
  tag?: string;
}

// Создаем базовый запрос с обработкой ошибок
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "http://localhost:3001/api",
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    headers.set("Accept", "application/json");
    return headers;
  },
});

// Обертка для обработки FormData - не устанавливаем Content-Type для FormData
const baseQueryWithFormData: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Если args - объект и body - FormData, не устанавливаем Content-Type
  if (typeof args === "object" && args.body instanceof FormData) {
    // Для FormData браузер сам установит правильный Content-Type с boundary
    return baseQuery(args, api, extraOptions);
  }
  
  // Для обычных запросов устанавливаем Content-Type: application/json
  if (typeof args === "object" && args.body && !(args.body instanceof FormData)) {
    const modifiedArgs = {
      ...args,
      headers: {
        ...(args.headers || {}),
        "Content-Type": "application/json",
      },
    };
    return baseQuery(modifiedArgs, api, extraOptions);
  }
  
  return baseQuery(args, api, extraOptions);
};

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQueryWithFormData(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    console.log("🔄 Token expired, logging out...");
    api.dispatch(logout());
  }


  return result;
};

export const traderApi = createApi({
  reducerPath: "traderApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "User",
    "Order",
    "Balance",
    "Trader",
    "Requisite",
    "PaymentMethod",
    "BalanceHistory",
    "Appeal",
    "Holder",
    "NewRequisite",
    "Currency",
    "Bank",
    "MobileOperator",
    "GeoLocation",
    "Device",
    "Tariff",
  ],
  endpoints: (builder) => ({
    // Аутентификация
    login: builder.mutation<any, { login: string; password: string }>({
      query: (credentials) => ({
        url: "/auth/login?requiredRole=TRADER",
        method: "POST",
        body: credentials,
      }),
      invalidatesTags: ["User", "Balance"],
    }),

    getProfile: builder.query<TraderProfile, void>({
      query: () => "/auth/profile",
      providesTags: ["User"],
      keepUnusedDataFor: 0,
    }),

    // 2FA endpoints
    setup2FA: builder.mutation<
      { secret: string; qrCode: string; otpauthUrl: string },
      void
    >({
      query: () => ({
        url: "/auth/2fa/setup",
        method: "POST",
      }),
    }),

    verify2FASetup: builder.mutation<
      { message: string; backupCodes: string[] },
      { code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/verify-setup",
        method: "POST",
        body,
      }),
    }),

    validate2FA: builder.mutation<
      { token: string; refreshToken: string; user: any },
      { tempToken: string; code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/validate",
        method: "POST",
        body,
      }),
    }),

    disable2FA: builder.mutation<
      { message: string },
      { password: string; code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/disable",
        method: "POST",
        body,
      }),
    }),

    regenerateBackupCodes: builder.mutation<
      { backupCodes: string[] },
      { code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/backup-codes",
        method: "POST",
        body,
      }),
    }),

    changePassword: builder.mutation<
      { message: string },
      { currentPassword: string; newPassword: string; confirmPassword: string }
    >({
      query: (body) => ({
        url: "/auth/change-password",
        method: "POST",
        body,
      }),
    }),

    get2FAStatus: builder.query<
      { enabled: boolean; backupCodesRemaining: number },
      void
    >({
      query: () => "/auth/2fa/status",
      providesTags: ["User"],
    }),

    // Баланс трейдера
    getBalance: builder.query<TraderBalance, void>({
      query: () => "/balance",
      providesTags: ["Balance"],
    }),

    getBalanceHistory: builder.query<PaginatedResponse<any>, any>({
      query: (params) => ({
        url: "/balance/history",
        params,
      }),
      providesTags: ["BalanceHistory"],
    }),

    // Ордера
    getOrders: builder.query<PaginatedResponse<any>, any>({
      query: (params) => ({
        url: "/orders",
        params,
      }),
      providesTags: ["Order"],
    }),

    getOrder: builder.query<any, string>({
      query: (orderId) => `/orders/${orderId}`,
      providesTags: ["Order"],
    }),

    updateOrderStatus: builder.mutation<
      any,
      { orderId: string; status: string }
    >({
      query: ({ orderId, status }) => ({
        url: `/orders/${orderId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Order"],
    }),

    uploadOrderReceipt: builder.mutation<
      any,
      { orderId: string; formData: FormData }
    >({
      query: ({ orderId, formData }) => ({
        url: `/orders/${orderId}/receipt`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Order"],
    }),

    manualConfirmOrder: builder.mutation<any, string>({
      query: (orderId) => ({
        url: `/orders/${orderId}/manual-confirm`,
        method: "POST",
      }),
      invalidatesTags: ["Order", "Balance"],
    }),

    // Реквизиты трейдера
    getTraderRequisites: builder.query<
      PaginatedResponse<TraderRequisite>,
      { traderId: string; params?: any }
    >({
      query: ({ traderId, params }) => ({
        url: `/traders/${traderId}/requisites`,
        params,
      }),
      providesTags: ["Requisite"],
    }),

    createRequisite: builder.mutation<
      TraderRequisite,
      { traderId: string; requisiteData: any }
    >({
      query: ({ traderId, requisiteData }) => ({
        url: `/traders/${traderId}/requisites`,
        method: "POST",
        body: requisiteData,
      }),
      invalidatesTags: ["Requisite"],
    }),

    updateRequisite: builder.mutation<
      TraderRequisite,
      { requisiteId: string; requisiteData: any }
    >({
      query: ({ requisiteId, requisiteData }) => ({
        url: `/traders/requisites/${requisiteId}`,
        method: "PATCH",
        body: requisiteData,
      }),
      invalidatesTags: ["Requisite"],
    }),

    updateRequisiteStatus: builder.mutation<
      TraderRequisite,
      { requisiteId: string; status: string }
    >({
      query: ({ requisiteId, status }) => ({
        url: `/traders/requisites/${requisiteId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Requisite"],
    }),

    // Доступные методы оплаты
    getAvailablePaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => "/traders/payment-methods/available",
      providesTags: ["PaymentMethod"],
    }),

    // Трейдер статистика
    getTraderStats: builder.query<any, string>({
      query: (traderId) => `/traders/${traderId}/stats`,
      providesTags: ["Trader"],
    }),

    // Тарифы трейдера
    getTraderTariffs: builder.query<any, string>({
      query: (traderId) => `/traders/${traderId}/tariffs`,
      providesTags: ["Tariff"],
    }),

    // Переключение статуса трейдера
    toggleMyStatus: builder.mutation<TraderProfile, void>({
      query: () => ({
        url: "/traders/my-status/toggle",
        method: "POST",
      }),
      invalidatesTags: ["User", "Trader"],
    }),

    // Апелляции (оставляем как есть)
    getAppeals: builder.query<PaginatedResponse<any>, any>({
      query: (params) => ({
        url: "/appeals",
        params,
      }),
      providesTags: ["Appeal"],
    }),

    getAppeal: builder.query<any, string>({
      query: (appealId) => `/appeals/${appealId}`,
      providesTags: ["Appeal"],
    }),

    createAppeal: builder.mutation<any, { orderId: string; files?: FormData }>({
      query: ({ orderId, files }) => ({
        url: `/appeals`,
        method: "POST",
        body: files || { orderId },
      }),
      invalidatesTags: ["Appeal", "Order"],
    }),

    updateAppealStatus: builder.mutation<
      any,
      { appealId: string; status: string; comment?: string }
    >({
      query: ({ appealId, status, comment }) => ({
        url: `/appeals/${appealId}/status`,
        method: "PATCH",
        body: { status, comment },
      }),
      invalidatesTags: ["Appeal"],
    }),

    // Трейдер одобряет апелляцию
    traderApproveAppeal: builder.mutation<any, string>({
      query: (appealId) => ({
        url: `/appeals/${appealId}/trader-approve`,
        method: "POST",
      }),
      invalidatesTags: ["Appeal", "Balance"],
    }),

    // Трейдер отклоняет апелляцию с доказательствами
    traderRejectAppeal: builder.mutation<
      any,
      { appealId: string; formData: FormData }
    >({
      query: ({ appealId, formData }) => ({
        url: `/appeals/${appealId}/trader-reject`,
        method: "POST",
        body: formData,
        formData: true,
      }),
      invalidatesTags: ["Appeal"],
    }),


    // Курсы валют
    getExchangeRate: builder.query<any, void>({
      query: () => "/exchange-rates/rapira/usdt-rub",
    }),

    // Сессии
    getActiveSessions: builder.query<any, void>({
      query: () => "/auth/sessions",
      providesTags: ["User"],
    }),

    terminateSession: builder.mutation<any, string>({
      query: (sessionId) => ({
        url: `/auth/sessions/${sessionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),

    logoutAllOtherSessions: builder.mutation<any, void>({
      query: () => ({
        url: "/auth/sessions/logout-others",
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),

    // Уведомления
    getNotifications: builder.query<any, void>({
      query: () => "/notifications",
    }),

    markNotificationAsRead: builder.mutation<any, string>({
      query: (notificationId) => ({
        url: `/notifications/${notificationId}/read`,
        method: "PATCH",
      }),
    }),

    // === НОВАЯ СИСТЕМА ДЕРЖАТЕЛЕЙ И РЕКВИЗИТОВ ===

    // Держатели реквизитов
    getTraderHolders: builder.query<
      PaginatedResponse<TraderRequisiteHolder>,
      { traderId: string } & GetHoldersParams
    >({
      query: ({ traderId, ...params }) => ({
        url: `/traders/${traderId}/holders`,
        params,
      }),
      providesTags: (result, error, { traderId }) => [
        { type: "Holder", id: "LIST" },
        { type: "Holder", id: traderId },
      ],
    }),

    getHolder: builder.query<TraderRequisiteHolder, string>({
      query: (holderId) => `/traders/holders/${holderId}`,
      providesTags: (_result, _error, id) => [{ type: "Holder", id }],
    }),

    createHolder: builder.mutation<
      TraderRequisiteHolder,
      { traderId: string; holderData: CreateHolderRequest }
    >({
      query: ({ traderId, holderData }) => ({
        url: `/traders/${traderId}/holders`,
        method: "POST",
        body: holderData,
      }),
      invalidatesTags: (result, error, { traderId }) => [
        { type: "Holder", id: "LIST" },
        { type: "Holder", id: traderId },
      ],
    }),

    updateHolder: builder.mutation<
      TraderRequisiteHolder,
      { holderId: string; holderData: UpdateHolderRequest }
    >({
      query: ({ holderId, holderData }) => ({
        url: `/traders/holders/${holderId}`,
        method: "PATCH",
        body: holderData,
      }),
      invalidatesTags: (_result, _error, { holderId }) => [
        { type: "Holder", id: holderId },
        { type: "Holder", id: "LIST" },
      ],
    }),

    toggleHolder: builder.mutation<
      TraderRequisiteHolder,
      { holderId: string; isActive: boolean }
    >({
      query: ({ holderId, isActive }) => ({
        url: `/traders/holders/${holderId}/toggle`,
        method: "POST",
        body: { isActive },
      }),
      invalidatesTags: (_result, _error, { holderId }) => [
        { type: "Holder", id: holderId },
        { type: "Holder", id: "LIST" },
        "NewRequisite",
      ],
    }),

    deleteHolder: builder.mutation<
      { success: boolean; message: string },
      { holderId: string; cascade?: boolean }
    >({
      query: ({ holderId, cascade = true }) => ({
        url: `/traders/holders/${holderId}`,
        method: "DELETE",
        params: { cascade },
      }),
      invalidatesTags: [{ type: "Holder", id: "LIST" }, "NewRequisite"],
    }),

    // Новые реквизиты
    getHolderRequisites: builder.query<
      PaginatedResponse<NewTraderRequisite>,
      { holderId: string } & GetRequisitesParams
    >({
      query: ({ holderId, ...params }) => ({
        url: `/traders/holders/${holderId}/requisites`,
        params,
      }),
      providesTags: ["NewRequisite"],
    }),

    getRequisiteById: builder.query<NewTraderRequisite, string>({
      query: (requisiteId) => `/traders/requisites/${requisiteId}`,
      providesTags: (_result, _error, id) => [{ type: "NewRequisite", id }],
    }),

    createNewRequisite: builder.mutation<
      NewTraderRequisite,
      { holderId: string; requisiteData: CreateRequisiteRequest }
    >({
      query: ({ holderId, requisiteData }) => ({
        url: `/traders/holders/${holderId}/requisites`,
        method: "POST",
        body: requisiteData,
      }),
      invalidatesTags: ["NewRequisite", { type: "Holder", id: "LIST" }],
    }),

    updateNewRequisite: builder.mutation<
      NewTraderRequisite,
      { requisiteId: string; requisiteData: Partial<CreateRequisiteRequest> }
    >({
      query: ({ requisiteId, requisiteData }) => ({
        url: `/traders/requisites/${requisiteId}`,
        method: "PATCH",
        body: requisiteData,
      }),
      invalidatesTags: (_result, _error, { requisiteId }) => [
        { type: "NewRequisite", id: requisiteId },
        "NewRequisite",
        "Holder",
      ],
    }),

    toggleNewRequisite: builder.mutation<
      NewTraderRequisite,
      { requisiteId: string; isActive: boolean }
    >({
      query: ({ requisiteId, isActive }) => ({
        url: `/traders/requisites/${requisiteId}/toggle`,
        method: "POST",
        body: { isActive },
      }),
      invalidatesTags: (_result, _error, { requisiteId }) => [
        { type: "NewRequisite", id: requisiteId },
        "NewRequisite",
        "Holder",
      ],
    }),

    deleteNewRequisite: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (requisiteId) => ({
        url: `/traders/requisites/${requisiteId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["NewRequisite", { type: "Holder", id: "LIST" }],
    }),

    // Валюты
    getCurrencies: builder.query<
      PaginatedResponse<Currency>,
      { page?: number; limit?: number; search?: string; isActive?: boolean }
    >({
      query: (params) => ({
        url: "/currencies",
        params,
      }),
      providesTags: ["Currency"],
    }),

    getActiveCurrencies: builder.query<Currency[], void>({
      query: () => "/currencies/active",
      providesTags: ["Currency"],
    }),

    // Банки
    getBanks: builder.query<Bank[], void>({
      query: () => "/banks/active",
      providesTags: ["Bank"],
    }),

    // VP-197: активные мобильные операторы для метода MOBCOM
    getActiveMobileOperators: builder.query<
      Array<{
        id: string;
        code: string;
        name: string;
        logoUrl: string | null;
        priority: number;
      }>,
      void
    >({
      query: () => "/mobile-operators/active",
      providesTags: ["MobileOperator"],
    }),

    // Геолокации
    getGeoLocations: builder.query<GeoLocation[], void>({
      query: () => "/geo-locations/active",
      providesTags: ["GeoLocation"],
    }),

    // === УСТРОЙСТВА АВТОМАТИКИ ===

    // Получение устройств держателя
    getDevices: builder.query<
      DevicesListResponse,
      { holderId?: string; page?: number; limit?: number; isActive?: boolean }
    >({
      query: (params) => ({
        url: "/devices",
        params,
      }),
      providesTags: (result, error, { holderId }) => [
        { type: "Device" as const, id: "LIST" },
        ...(holderId ? [{ type: "Device" as const, id: holderId }] : []),
      ],
    }),

    // Получение информации об устройстве
    getDevice: builder.query<DeviceInfo, string>({
      query: (deviceId) => `/devices/${deviceId}`,
      providesTags: (_result, _error, id) => [{ type: "Device" as const, id }],
    }),

    // Создание токена привязки
    createBindingToken: builder.mutation<
      BindingTokenResponse,
      { holderId: string }
    >({
      query: (body) => ({
        url: "/devices/bind-token",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Device" as const],
    }),

    // Отзыв устройства
    revokeDevice: builder.mutation<
      { message: string },
      { deviceId: string; reason?: string }
    >({
      query: ({ deviceId, reason }) => ({
        url: `/devices/${deviceId}`,
        method: "DELETE",
        body: { reason },
      }),
      invalidatesTags: ["Device" as const, "Holder" as const],
    }),

    // Заявки на вывод трейдера
    createWorkingWithdrawal: builder.mutation<
      any,
      { amount: number; currency: string; walletAddress: string; comment?: string }
    >({
      query: (body) => ({
        url: "/trader-withdrawals/working",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Balance" as const],
    }),

    createTransitWithdrawal: builder.mutation<
      any,
      { amount: number; currency: string; walletAddress: string; comment?: string }
    >({
      query: (body) => ({
        url: "/trader-withdrawals/transit",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Balance" as const],
    }),

    getTraderWithdrawals: builder.query<any, void>({
      query: () => "/trader-withdrawals",
      providesTags: ["Balance" as const],
    }),

    getTransitWithdrawalAvailability: builder.query<any, void>({
      query: () => "/trader-withdrawals/transit/availability",
    }),

    // Settings - Withdrawal Fee
    getWithdrawalFee: builder.query<{ fee: number }, void>({
      query: () => "/settings/withdrawal-fee",
    }),
  }),
});

export const {
  // Auth hooks
  useLoginMutation,
  useGetProfileQuery,

  // 2FA hooks
  useSetup2FAMutation,
  useVerify2FASetupMutation,
  useValidate2FAMutation,
  useDisable2FAMutation,
  useRegenerateBackupCodesMutation,
  useChangePasswordMutation,
  useGet2FAStatusQuery,

  // Balance hooks
  useGetBalanceQuery,
  useGetBalanceHistoryQuery,

  // Orders hooks
  useGetOrdersQuery,
  useGetOrderQuery,

  useUpdateOrderStatusMutation,
  useUploadOrderReceiptMutation,
  useManualConfirmOrderMutation,

  // Requisites hooks
  useGetTraderRequisitesQuery,
  useCreateRequisiteMutation,
  useUpdateRequisiteMutation,
  useUpdateRequisiteStatusMutation,

  // Payment methods hooks
  useGetAvailablePaymentMethodsQuery,

  // Trader stats hooks
  useGetTraderStatsQuery,
  useGetTraderTariffsQuery,
  useToggleMyStatusMutation,

  // Appeals hooks
  useGetAppealsQuery,
  useGetAppealQuery,
  useCreateAppealMutation,
  useUpdateAppealStatusMutation,
  useTraderApproveAppealMutation,
  useTraderRejectAppealMutation,

  // Exchange rate hooks
  useGetExchangeRateQuery,

  // Sessions hooks
  useGetActiveSessionsQuery,
  useTerminateSessionMutation,
  useLogoutAllOtherSessionsMutation,

  // Notifications hooks
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,

  // === НОВЫЕ ХУКИ ДЛЯ ДЕРЖАТЕЛЕЙ И РЕКВИЗИТОВ ===

  // Holders hooks
  useGetTraderHoldersQuery,
  useGetHolderQuery,
  useCreateHolderMutation,
  useUpdateHolderMutation,
  useToggleHolderMutation,
  useDeleteHolderMutation,

  // New requisites hooks
  useGetHolderRequisitesQuery,
  useGetRequisiteByIdQuery,
  useCreateNewRequisiteMutation,
  useUpdateNewRequisiteMutation,
  useToggleNewRequisiteMutation,
  useDeleteNewRequisiteMutation,

  // Reference data hooks
  useGetCurrenciesQuery,
  useGetActiveCurrenciesQuery,
  useGetBanksQuery,
  useGetActiveMobileOperatorsQuery, // VP-197
  useGetGeoLocationsQuery,

  // Device hooks
  useGetDevicesQuery,
  useGetDeviceQuery,
  useCreateBindingTokenMutation,
  useRevokeDeviceMutation,

  // Withdrawal hooks
  useCreateWorkingWithdrawalMutation,
  useCreateTransitWithdrawalMutation,
  useGetTraderWithdrawalsQuery,
  useGetTransitWithdrawalAvailabilityQuery,

  // Settings hooks
  useGetWithdrawalFeeQuery,
} = traderApi;
